import {UUID} from "node:crypto";

export type IBookInstanceStatus =
    | 'IN_DEPOSIT'
    | 'RESERVED'
    | 'RENTED'
    | 'RETRIEVED'

export interface INameId {
    id?: UUID
    name: string
}

export interface IInstitutionGroup {
    id?: UUID
    institutionId?: UUID
    name: string
}

export interface IInstitutionGroupDetails {
    id?: UUID
    name?: string
    institutionId?: UUID
    institutionName?: string
}


export interface IAuthor {
    id?: UUID
    name: string
    translations: IAuthorTranslation[]
}

export interface IAuthorTranslation {
    id?: UUID
    locale: string
    description: string
    motto: string
}

export interface IAgeCategory {
    id?: UUID
    name: string
    startAge: number
    endAge: number
}

export interface IInternalAgeGroup {
    id?: UUID
    name: string
    startAge: number
    endAge: number
}


export interface IPhoto {
    id?: UUID
    key: string
    url: string
    main: boolean
    index: number
}

export interface IBookInstance {
    id: UUID
    uniqueReadableId: string
    status: IBookInstanceStatus
    rentCounter: number
    catalogPrice: number
    boughtPrice: number
    institution: IInstitutionInBookInstance
    institutionGroupId: string
}

export interface IBook {
    id?:UUID
    name: string
    description?: string
    publicationYear?: number
    language?: INameId
    publisher?: INameId
    isbn?: string
    bookCollection?: INameId
    numberOfPages?: number
    stock?: number
    rented?: number
    reserved?: number
    illustrators?: INameId[]
    translators?: INameId[]
    authors?: IAuthor[]
    categories?: INameId[]
    genres?: INameId[]
    ageCategories?: IAgeCategory[]
    ageGroups?: IInternalAgeGroup[]
    photos?: IPhoto[]
    instances?: IBookInstance[]
    height?: number
    width?: number
    catalogPrice?: number
    salePrice?: number
    publisherLink?: string
    weight?: number
    hiddenBook?: boolean
}

export interface IAddress {
    id?: UUID
    street?: string
    city?: string
    country?: string
}

export interface IInstitution {
    id?: UUID
    name?: string
    phone?: string
    email?: string
    readableIdPrefix?: string
    address?: IAddress
    bookInstances?: INameId[]
    type?: string
}

export interface IInstitutionInBookInstance {
    id?: UUID
    name?: string
    readableIdPrefix?: string
}

export interface Image {
    photoUrl: string
    photoKey: string
}

export interface IReadingPlanTemplate {
    id?: UUID
    label?: string
    groups?: number
    booksInGroup?: number
    booksBackupGroup?: number
    internalAgeGroup?: IInternalAgeGroup
    readingPlanTemplateGroups?: IReadingPlanTemplateGroup[]
}

export interface IReadingPlanActivate {
    success?: boolean
    message?: string
}

export interface IReadingPlanTemplateGroup {
    id?: UUID
    readingPlanTemplateId?: UUID
    backupGroup: boolean
    color: IColor
    books: IBookInPlanGroup[]
    catalogPrice: number
    boughtPrice: number
}

export interface IReadingPlanTemplateLabel {
    label: string
}

export interface IBookInPlanGroup {
    id?: UUID
    name?: string
    photos?: IPhoto[]
    catalogPrice: number
    boughtPrice: number
    salePrice: number
    hasQuestionsAndActivities: boolean
    numberOfInstances: number
}

export interface IColor {
    id?: UUID
    code?: string
    name?: string
}

export interface ICreateTemplatePlan {
    groups:number
    booksInGroup:number
    booksInBackupGroup:number
    autoPopulate: boolean
    label:string
    internalAgeGroupId: string
}

export interface IReadingPlanSummary {
    id?: UUID
    startDate: number
    weekFrequency: number
    numberOfSwitches: number
    scheduled: boolean
    assignedUsers: boolean
    active: boolean
    numberOfUsers: boolean
    booksMissing?: IBookInPlanGroup[]
}

export interface IReadingPlanRequest {
    templateId?: string
    weekFrequency: number
    numberOfSwitches: number
    createSchedule: boolean
    assignUsers: boolean
    startDateTimestamp: number
    initialPackagePrice: number
    subscriptionPrice: number
    packageInstallmentAmount: number
    platformSubscriptionAmount: number
}

export interface XmasPackageListEntry {
    id?: string
    name?: string
    packageId?: string
    status: string
    orderDispNumber: string
    userName: string
    userPhone: string
    createdAt: number
}

export interface OrderListEntry {
    id?: string
    status: string
    orderDispNumber: string
    userName: string
    userPhone: string
    type: string,
    fullPrice: number
    createdAt: number
    integratedIn2Inv: boolean
}

export interface BookInPackageDetails {
    bookId: string
    instanceId: string
    name: string
    catalogPrice: number
    boughtPrice: number
    url: string
}

export interface BooksInPackage {
    selectedBook: BookInPackageDetails
    suggestions: BookInPackageDetails[]
}
export interface DeliveryAddress {
    firstName: string
    lastName: string
    email: string
    phone: string
    city: string
    county: string
    country: string
    postCode: string
    address: string
}
export interface XmasPackageDetails {
    id: string
    name: string
    packageId?: string
    status: string
    orderDispNumber: string
    orderNote: string
    amountPaid: number
    ageGroupId: string
    awb: string
    ageGroupName: string
    userName: string
    userPhone: string
    books: BooksInPackage[]
    deliveryAddress: DeliveryAddress
}

export interface PaymentDto {
    id: string
    intervalStart: string
    intervalEnd: string
    paid: boolean
    amount: number
}

export interface SubscriptionDto {
    id: string
    type: string
    recurrenceType: string

    startDate: string
    endDate: string
    active: boolean
    // kidProfile:
    payment: PaymentDto

}

export interface OrderDetails {
    id: string
    orderDispNumber: string
    orderNote: string
    status: string
    type: string
    userName: string
    userPhone: string
    deliveryAddress: DeliveryAddress
    fullPrice: number
    awb: string
    books: BookInPackageDetails[]
    packages: XmasPackageDetails[]
    subscription: SubscriptionDto
    transportPrice: number
    voucherValue: number
    voucherCode: string
    percentage: boolean
}


export interface UploadedPhoto {
    name: string
    key: string
    url: string
    main: boolean
    index: number
}

export interface PackageConfig {
    id: string
    name: string
    description: string
    numberOfBooks: number
    price: number
    type: string
    discountPrice: number
    active: boolean
    ageGroup: IInternalAgeGroup
    photos: IPhoto[]
    selectedEntries: Record<string, number>
    weight: number
    slug: string
    diy: boolean
    resources: IResource[]
}

export interface ICreatePackageConfig {
    id?: string
    name: string
    description: string
    numberOfBooks: number
    price: number
    discountPrice: number
    active: boolean
    type: string
    ageGroup: IInternalAgeGroup
    selectedEntries: Record<string, number>
    photos: IPhoto[]
    weight: number
    slug: string
    diy: boolean
    resources: IResource[]
}

export interface UserKidInGroup {
    id?: UUID
    firstName: string
    lastName: string
    phone: string
    email: string
    kid: KidInGroup
}

export interface KidInGroup {
    id?: UUID
    name: string
    birthdate: string
    groupDetails? : IInstitutionGroupDetails
}

export interface IScheduleEntry {
    id: UUID
    index: number
    startDate: number
    endDate: number
    active: boolean
}

export interface IUserGroupAssignment {
    id: UUID
    kidName: string
    firstName: string
    lastName: string
    groupId: UUID
    groupColor: string
    groupIndex: number
    bookNames: string[]
    groupStatus: string
    additionalBookName?: string
    additionalBookColor?: string
    additionalBookStatus?: string
}

export interface IScheduleEntryAssignments {
    scheduleEntryId: UUID
    index: number
    startDate: number
    endDate: number
    assignments: IUserGroupAssignment[]
}

export interface IBlogPost {
    id?: UUID
    title: string
    slug?: string
    content?: string
    excerpt?: string
    coverImageUrl?: string
    published: boolean
    isPrivate: boolean
    createdAt?: number
    authorId?: UUID
    authorName?: string
    categoryIds?: UUID[]
    categoryNames?: string[]
    metaTitle?: string
    metaDescription?: string
}

export interface IBlogCategory {
    id?: UUID
    name: string
    slug?: string
    visible?: boolean
}

export interface IAuthorProfile {
    id?: UUID
    authorName: string
    subheading?: string
    coverImageUrl?: string
    userId?: UUID
    userEmail?: string
    userDisplayName?: string
}

export interface IUserSearchResult {
    id: UUID
    email: string
    displayName: string
}

export type ContactUsStatus = 'NOT_CONTACTED' | 'IN_PROGRESS' | 'CONTACTED' | 'SOLVED'

export type PlannedPaymentStatus = 'PAID' | 'TO_BE_PAID' | 'OVERDUE'

export interface IPlannedPayment {
    id: UUID
    userName: string
    kidName: string
    amount: number
    paymentDate: string
    intervalStart: string
    intervalEnd: string
    status: PlannedPaymentStatus
}

export type DiscountType = 'FIXED' | 'PERCENTAGE'

export interface IVoucher {
    id: UUID
    code: string
    discount: number
    discountPercentage: number
    expireDate: string  // ISO instant string
    userId: UUID | null
    userDisplayName: string | null
    userEmail: string | null
    usageCount: number
}

export interface ICreateVoucher {
    code: string
    discountType: DiscountType
    discountValue: number
    expireDate: string  // ISO instant string
    userId: UUID | null
}

export interface IUpdateVoucher {
    code: string
    discountType: DiscountType
    discountValue: number
    expireDate: string  // ISO instant string
    userId: UUID | null
}

export interface IContactUsRequest {
    id: UUID
    firstName: string
    lastName: string
    email: string
    phone: string
    city: string
    kindergarten: string
    role: string
    contactedStatus: ContactUsStatus
    notes: string | null
    createdAt: number
}

export interface IUpdateContactUsRequest {
    contactedStatus: ContactUsStatus
    notes: string
}

export type OnlineSubscriptionType = 'GENERAL_ACCESS' | 'PREMIUM_ACCESS'

export interface IOnlineSubscription {
    id: UUID
    name: string
    description: string | null
    type: OnlineSubscriptionType
    priceWithoutDiscount: number
    discountPercentage: number
    paymentInterval: number
    active: boolean
    photoUrl: string | null
    deleted: boolean
}

export interface ICreateOnlineSubscription {
    name: string
    description: string
    type: OnlineSubscriptionType
    priceWithoutDiscount: number
    discountPercentage: number
    paymentInterval: number
    photoUrl: string
}

export interface IUpdateOnlineSubscription {
    name: string
    description: string
    type: OnlineSubscriptionType
    priceWithoutDiscount: number
    discountPercentage: number
    paymentInterval: number
    photoUrl: string
    active: boolean
}

export interface IPlatformConfig {
    transportCost: number
    freeTransportCutoff: number
    individualSalesEnable: boolean
    onlineSubscriptionsEnabled: boolean
}

export type ResourceType =
    | 'PARENTS_GUIDE'
    | 'ENGLISH_PARENTS_GUIDE'
    | 'PARENTS_GUIDE_CLUBS'
    | 'PARENTS_GUIDE_SCHOOL_CLUBS'
    | 'TEN_DAYS_CHALLENGE'
    | 'KINDERGARTEN_GUIDE'
    | 'BOOK_SHEET'
    | 'XMAS_ADVENT'

export const ALL_RESOURCE_TYPES: ResourceType[] = [
    'PARENTS_GUIDE',
    'ENGLISH_PARENTS_GUIDE',
    'PARENTS_GUIDE_CLUBS',
    'PARENTS_GUIDE_SCHOOL_CLUBS',
    'TEN_DAYS_CHALLENGE',
    'KINDERGARTEN_GUIDE',
    'BOOK_SHEET',
    'XMAS_ADVENT',
]

export interface IResource {
    id: UUID
    name: string
    key: string
    type: ResourceType
}

export interface ICreateResource {
    name: string
    key: string
    type: ResourceType
}